/**
 * Item-Shop (Masterprompt Abschnitt 66,
 * VISUAL_REFERENCE Abschnitte 46a und 46d).
 *
 * Aufbau seit v0.35 - derselbe Rahmen wie Send Wave, Defense und Skills:
 *
 *   Kopf     Titel, eine Zeile Erklaerung, SLOTS n / 4, grosses X
 *   Links    die vier Slots als 2x2-Quadrat, daneben die Items als Karten
 *            (Bild links, Text rechts), daneben die Consumables als hohe
 *            Kacheln - mit dem Revive Totem (18.10.2026)
 *   Rechts   das angetippte Item im Detail und der eine goldene Knopf
 *
 * Antippen waehlt aus, gekauft wird nur rechts - wie in den anderen Menues.
 * Vorher stand auf jeder Karte ein Kauf- und ein Verkaufsknopf.
 *
 * Die drei Stufen eines Items stehen als Kette von Punkten auf der Karte. Sie
 * ersetzen einander: Stufe 2 kann alles, was Stufe 1 konnte. Deshalb zeigt die
 * Detailspalte immer genau EINE Stufe - die, um die es gerade geht.
 *
 * Alles hier ist Vorschau: Ob ein Kauf zustande kommt, entscheidet der Server.
 */

import {
  CONSUMABLES,
  HERO_ITEM_SLOTS,
  ITEM_DELIVERY_SECONDS,
  ITEMS,
  REVIVE_TOTEM,
  REVIVE_TOTEM_RESPAWN_SECONDS,
  type ConsumableDefinition,
  type ItemDefinition,
} from '@dueline/game-data';
import {
  checkItemPurchase,
  checkItemUpgrade,
  getItemStageCost,
  getMaxStage,
  isBattlePhase,
} from '@dueline/game-rules';
import type { MatchPhase } from '@dueline/shared';
import { useState } from 'react';
import { t } from '../i18n/index.js';
import type { MatchSnapshot, OwnedItemView } from '../net/MatchConnection.js';
import { formatGold } from './economyView.js';
import {
  buildEffectLines,
  buildStagePips,
  sellValue,
  stageToShow,
  type EffectLabels,
} from './itemView.js';
import { useTap } from './usePress.js';
import { gegenstandBild, trankBild } from './faehigkeitBilder.js';
import type { Bildquellen } from './menueBilder.js';

/** Alles, was einen Slot belegt - die sechs Items und das Totem. */
const SLOT_ITEMS: readonly ItemDefinition[] = [...ITEMS, REVIVE_TOTEM];

/** Die Gegenstaende ohne das Totem - das steht bei den Consumables. */
const KARTEN_ITEMS: readonly ItemDefinition[] = ITEMS;

/** Ein Bild als <picture>, wie ueberall in der Oberflaeche. */
function Bild({ bild, klasse }: { bild: Bildquellen; klasse: string }) {
  return (
    <picture className={klasse}>
      <source type="image/avif" srcSet={bild.avif} sizes="128px" />
      <source type="image/webp" srcSet={bild.webp} sizes="128px" />
      <img src={bild.ersatz} alt="" decoding="async" />
    </picture>
  );
}

/**
 * Das grosse, eckige Bild eines Gegenstandes auf Karte und Ausruestungsplatz
 * (18.10.2026). Der Rahmen hat die Farbe der Kategorie. Ohne Bild steht wie
 * frueher das Kuerzel.
 */
function GegenstandFlaeche({ item, klasse }: { item: ItemDefinition; klasse: string }) {
  const bild = gegenstandBild(item.id);
  return (
    <span className={`${klasse} dl-item-rahmen--${item.category}`} aria-hidden="true">
      {bild ? (
        <Bild bild={bild} klasse="dl-item-rahmen__bild" />
      ) : (
        <span className="dl-item-rahmen__kuerzel">{item.name.slice(0, 2).toUpperCase()}</span>
      )}
    </span>
  );
}

/**
 * Das Bild des Gegenstandes im Kreis (18.10.2026); ohne Bild wie bisher das
 * Kuerzel auf der Farbe der Kategorie.
 */
function ItemToken({ item }: { item: ItemDefinition }) {
  const bild = gegenstandBild(item.id);
  return (
    <span
      className={
        `dl-creep-token dl-item-token--${item.category}` + (bild ? ' dl-item-token--bild' : '')
      }
      aria-hidden="true"
    >
      {bild ? (
        <picture className="dl-item-token__bild">
          <source type="image/avif" srcSet={bild.avif} sizes="64px" />
          <source type="image/webp" srcSet={bild.webp} sizes="64px" />
          <img src={bild.ersatz} alt="" decoding="async" />
        </picture>
      ) : (
        item.name.slice(0, 2).toUpperCase()
      )}
    </span>
  );
}

/** Die Stufen eines Items als Kette. */
function StageChain({ item, stage }: { item: ItemDefinition; stage: number }) {
  const pips = buildStagePips(stage, getMaxStage(item.id), (index) =>
    getItemStageCost(item.id, index),
  );

  return (
    <span className="dl-stage-chain" aria-hidden="true">
      {pips.map((pip) => (
        <span key={pip.stage} className={`dl-stage-pip dl-stage-pip--${pip.state}`} />
      ))}
    </span>
  );
}

function ItemCard({
  item,
  besitz,
  gewaehlt,
  bezahlbar,
  onSelect,
}: {
  item: ItemDefinition;
  besitz: OwnedItemView | undefined;
  gewaehlt: boolean;
  bezahlbar: boolean;
  onSelect: () => void;
}) {
  const press = useTap(onSelect);
  const stufe = besitz?.stage ?? 0;
  const preis = getItemStageCost(item.id, stufe);
  const voll = stufe >= getMaxStage(item.id);

  /*
   * Bild links, so hoch wie die Karte; rechts Name, Preis und Stufen
   * (18.10.2026). Vorher sass das Bild klein in einem leeren Balken, und die
   * Kategorie stand als eigene Zeile darunter - sie ist jetzt die Farbe des
   * Bildrahmens und steht als Wort in der Detailansicht.
   */
  return (
    <button
      type="button"
      className={
        'dl-item-card' +
        (gewaehlt ? ' dl-item-card--active' : '') +
        (besitz ? ' dl-item-card--owned' : '') +
        (bezahlbar || voll ? '' : ' dl-item-card--poor')
      }
      aria-pressed={gewaehlt}
      aria-label={item.name}
      {...press}
    >
      <GegenstandFlaeche item={item} klasse="dl-item-card__bild dl-item-rahmen" />

      <span className="dl-item-card__text">
        <span className="dl-item-card__name">{item.name}</span>
        {/* Preis der naechsten Stufe - voll ausgebaut steht dort ein Haken. */}
        <span className="dl-creep-card__cost">{voll ? '✓' : preis}</span>
        <StageChain item={item} stage={stufe} />
      </span>
    </button>
  );
}

/**
 * Eine hohe Kachel bei den Consumables (18.10.2026): Name oben, grosses
 * Bild, unten Preis und Vorrat - bzw. beim Totem das Schild "1 SLOT".
 */
function Kachel({
  name,
  bild,
  kuerzel,
  preis,
  info,
  infoKlasse,
  gewaehlt,
  besitz,
  onSelect,
}: {
  name: string;
  bild: Bildquellen | undefined;
  kuerzel: string;
  preis: string;
  info: string;
  infoKlasse: string;
  gewaehlt: boolean;
  besitz: boolean;
  onSelect: () => void;
}) {
  const press = useTap(onSelect);

  return (
    <button
      type="button"
      className={
        'dl-consumable dl-consumable--kachel' +
        (gewaehlt ? ' dl-consumable--active' : '') +
        (besitz ? ' dl-consumable--owned' : '')
      }
      aria-pressed={gewaehlt}
      aria-label={name}
      {...press}
    >
      <span className="dl-consumable__name">{name}</span>
      <span className="dl-consumable__flaeche" aria-hidden="true">
        {bild ? (
          <Bild bild={bild} klasse="dl-consumable__bild" />
        ) : (
          <span className="dl-item-rahmen__kuerzel">{kuerzel}</span>
        )}
      </span>
      <span className="dl-consumable__fuss">
        <span className="dl-creep-card__cost">{preis}</span>
        <span className={infoKlasse}>{info}</span>
      </span>
    </button>
  );
}

/** Ein Trank. Belegt keinen Slot. */
function ConsumableCard({
  consumable,
  vorrat,
  gewaehlt,
  onSelect,
}: {
  consumable: ConsumableDefinition;
  vorrat: number;
  gewaehlt: boolean;
  onSelect: () => void;
}) {
  return (
    <Kachel
      name={consumable.name}
      bild={trankBild(consumable.id)}
      kuerzel={consumable.restores === 'hp' ? 'HP' : 'MP'}
      preis={String(consumable.cost)}
      info={`${vorrat} / ${consumable.maxCharges}`}
      infoKlasse="dl-consumable__stock"
      gewaehlt={gewaehlt}
      besitz={false}
      onSelect={onSelect}
    />
  );
}

/**
 * Das Revive Totem als Kachel bei den Consumables (Rueckmeldung vom
 * 18.10.2026): Es wird nicht getrunken, aber verbraucht. Es belegt weiter
 * einen Item-Platz - das sagt das Schild "1 SLOT".
 */
function TotemCard({
  besitz,
  gewaehlt,
  onSelect,
}: {
  besitz: OwnedItemView | undefined;
  gewaehlt: boolean;
  onSelect: () => void;
}) {
  const stufe = besitz?.stage ?? 0;
  const voll = stufe >= getMaxStage(REVIVE_TOTEM.id);
  return (
    <Kachel
      name={REVIVE_TOTEM.name}
      bild={gegenstandBild(REVIVE_TOTEM.id)}
      kuerzel="RE"
      preis={voll ? '✓' : String(getItemStageCost(REVIVE_TOTEM.id, stufe))}
      info={t.shop.slotBadge}
      infoKlasse="dl-consumable__slot"
      gewaehlt={gewaehlt}
      besitz={besitz !== undefined}
      onSelect={onSelect}
    />
  );
}

/**
 * Das runde Zeichen eines Trankes: das Bild (16.10.2026), sonst wie bisher
 * HP oder MP auf der Typfarbe.
 */
function TrankZeichen({ trank }: { trank: (typeof CONSUMABLES)[number] }) {
  const bild = trankBild(trank.id);
  return (
    <span
      className={
        `dl-creep-token dl-consumable-token--${trank.restores}` +
        (bild ? ' dl-consumable-token--bild' : '')
      }
      aria-hidden="true"
    >
      {bild ? (
        <picture className="dl-consumable-token__bild">
          <source type="image/avif" srcSet={bild.avif} sizes="64px" />
          <source type="image/webp" srcSet={bild.webp} sizes="64px" />
          <img src={bild.ersatz} alt="" decoding="async" />
        </picture>
      ) : trank.restores === 'hp' ? (
        'HP'
      ) : (
        'MP'
      )}
    </span>
  );
}

/**
 * Ein Ausruestungsplatz.
 *
 * Belegte Plaetze sind Schaltflaechen: Ein Tipp darauf zeigt dasselbe rechts
 * an wie ein Tipp auf die Karte weiter unten (Rueckmeldung vom 27.09.2026).
 * Man sucht sonst in der Kartenreihe nach dem, was man schon traegt.
 */
function EquipmentSlot({
  item,
  stage,
  gewaehlt,
  onSelect,
}: {
  item: ItemDefinition | undefined;
  stage: number;
  gewaehlt: boolean;
  onSelect: () => void;
}) {
  const press = useTap(onSelect);

  if (!item) {
    return (
      <div className="dl-shop__slot" aria-hidden="true">
        <span className="dl-shop__slot-empty">+</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={'dl-shop__slot dl-shop__slot--filled' + (gewaehlt ? ' dl-shop__slot--active' : '')}
      aria-pressed={gewaehlt}
      aria-label={t.shop.slotOf.replace('{name}', item.name)}
      {...press}
    >
      <GegenstandFlaeche item={item} klasse="dl-shop__slot-bild dl-item-rahmen" />
      <StageChain item={item} stage={stage} />
    </button>
  );
}

/** Was gerade rechts steht: ein Slot-Item oder ein Trank. */
type Auswahl = { art: 'item'; id: string } | { art: 'trank'; id: string };

export function ItemShop({
  snapshot,
  onBuy,
  onSell,
  onBuyConsumable,
  onClose,
}: {
  snapshot: MatchSnapshot;
  onBuy: (itemId: string) => void;
  onSell: (itemId: string) => void;
  onBuyConsumable: (itemId: string) => void;
  onClose: () => void;
}) {
  const [auswahl, setAuswahl] = useState<Auswahl>({ art: 'item', id: SLOT_ITEMS[0]?.id ?? '' });

  const kampf = isBattlePhase(snapshot.phase as MatchPhase);
  const belegt = snapshot.items.length;
  const besitzVon = (id: string) => snapshot.items.find((eintrag) => eintrag.itemId === id);

  const schliessen = useTap(onClose);

  const labels = t.shop.effects as EffectLabels;

  /* --- Was rechts steht ------------------------------------------------- */
  const item =
    auswahl.art === 'item' ? SLOT_ITEMS.find((eintrag) => eintrag.id === auswahl.id) : undefined;
  const trank =
    auswahl.art === 'trank' ? CONSUMABLES.find((eintrag) => eintrag.id === auswahl.id) : undefined;

  const besitz = item ? besitzVon(item.id) : undefined;
  const stufe = besitz?.stage ?? 0;
  const maxStufe = item ? getMaxStage(item.id) : 0;
  const voll = item ? stufe >= maxStufe : false;
  const zeigeStufe = item ? stageToShow(item, stufe) : 1;
  const wirkung = item ? buildEffectLines(item.stages[zeigeStufe - 1] ?? {}, labels) : [];

  const trankVorrat = (id: string) =>
    id === 'health_potion' ? snapshot.healthPotions : snapshot.manaPotions;
  /*
   * Unterwegs befindliche Traenke zaehlen mit (Produktentscheidung vom
   * 05.10.2026) - genau wie auf dem Server. Sonst boete der Shop einen
   * dritten Kauf an, den der Server dann ablehnt.
   */
  const trankBelegt = (id: string) =>
    trankVorrat(id) +
    (id === 'health_potion' ? snapshot.healthPotionsIncoming : snapshot.manaPotionsIncoming);
  /** Restzeit des naechsten unterwegs befindlichen Trankes dieser Art. */
  const trankAnkunft = (id: string) =>
    id === 'health_potion' ? snapshot.healthPotionNextSeconds : snapshot.manaPotionNextSeconds;

  /* --- Was der Knopf tut ------------------------------------------------ */
  const pruefung = item
    ? besitz
      ? checkItemUpgrade(snapshot.gold, snapshot.items, item.id)
      : checkItemPurchase(snapshot.gold, snapshot.items, item.id)
    : trank
      ? {
          allowed: snapshot.gold >= trank.cost && trankBelegt(trank.id) < trank.maxCharges,
          reason:
            snapshot.gold < trank.cost ? ('not_enough_gold' as const) : ('no_charges' as const),
          cost: trank.cost,
        }
      : { allowed: false, reason: null, cost: 0 };

  const grund = !kampf
    ? t.shop.blocked.match_not_running
    : pruefung.allowed || voll
      ? null
      : ((t.shop.blocked as Record<string, string>)[pruefung.reason ?? ''] ?? null);

  const gesperrt = !kampf || voll || !pruefung.allowed;

  const kaufen = useTap(() => {
    if (gesperrt) return;
    if (item) onBuy(item.id);
    if (trank) onBuyConsumable(trank.id);
  });

  const verkaufen = useTap(() => {
    if (item && besitz) onSell(item.id);
  });

  const knopfText = voll ? t.shop.cta.maxed : besitz ? t.shop.cta.upgrade : t.shop.cta.buy;
  const preis = item ? getItemStageCost(item.id, stufe) : (trank?.cost ?? 0);

  return (
    <div className="dl-composer dl-shop" role="dialog" aria-label={t.shop.title}>
      <div className="dl-composer__head dl-composer__head--limit">
        <span className="dl-composer__title">{t.shop.title}</span>
        <span className="dl-composer__subtitle">
          {t.shop.subtitle.replace('{s}', String(ITEM_DELIVERY_SECONDS))}
        </span>
        <span
          className={`dl-composer__limit${belegt >= HERO_ITEM_SLOTS ? ' dl-composer__limit--over' : ''}`}
        >
          <span className="dl-composer__limit-label">{t.shop.slotsLabel}</span>
          <span className="dl-composer__limit-value">
            {belegt} / {HERO_ITEM_SLOTS}
          </span>
        </span>
        <button
          type="button"
          className="dl-composer__close"
          aria-label={t.shop.close}
          {...schliessen}
        >
          ✕
        </button>
      </div>

      <div className="dl-composer__main dl-shop__main">
        {/* Die vier Slots als 2x2-Quadrat - wie in der Referenz (18.10.2026). */}
        <div className="dl-shop__equipment">
          <span className="dl-summary__title">{t.shop.equipment}</span>
          <div className="dl-shop__slots">
            {Array.from({ length: HERO_ITEM_SLOTS }, (_, index) => {
              const eintrag = snapshot.items[index];
              const gefunden = eintrag
                ? SLOT_ITEMS.find((definition) => definition.id === eintrag.itemId)
                : undefined;

              return (
                <EquipmentSlot
                  key={index}
                  item={gefunden}
                  stage={eintrag?.stage ?? 0}
                  gewaehlt={
                    gefunden !== undefined && auswahl.art === 'item' && auswahl.id === gefunden.id
                  }
                  onSelect={() => {
                    if (gefunden) setAuswahl({ art: 'item', id: gefunden.id });
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="dl-shop__lists">
          <div className="dl-shop__group">
            <span className="dl-summary__title">{t.shop.itemsTitle}</span>
            <div className="dl-shop__cards">
              {KARTEN_ITEMS.map((eintrag) => (
                <ItemCard
                  key={eintrag.id}
                  item={eintrag}
                  besitz={besitzVon(eintrag.id)}
                  gewaehlt={auswahl.art === 'item' && auswahl.id === eintrag.id}
                  bezahlbar={
                    snapshot.gold >= getItemStageCost(eintrag.id, besitzVon(eintrag.id)?.stage ?? 0)
                  }
                  onSelect={() => setAuswahl({ art: 'item', id: eintrag.id })}
                />
              ))}
            </div>
          </div>

          <div className="dl-shop__group dl-shop__group--consumables">
            <span className="dl-summary__title">{t.shop.consumables}</span>
            <div className="dl-shop__consumables">
              {CONSUMABLES.map((eintrag) => (
                <ConsumableCard
                  key={eintrag.id}
                  consumable={eintrag}
                  vorrat={trankVorrat(eintrag.id)}
                  gewaehlt={auswahl.art === 'trank' && auswahl.id === eintrag.id}
                  onSelect={() => setAuswahl({ art: 'trank', id: eintrag.id })}
                />
              ))}
              <TotemCard
                besitz={besitzVon(REVIVE_TOTEM.id)}
                gewaehlt={auswahl.art === 'item' && auswahl.id === REVIVE_TOTEM.id}
                onSelect={() => setAuswahl({ art: 'item', id: REVIVE_TOTEM.id })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="dl-composer__side">
        <span className="dl-summary__title">{t.shop.selected}</span>

        {item && (
          <>
            <div className="dl-detail__head">
              <ItemToken item={item} />
              <span className="dl-detail__name">{item.name}</span>
            </div>

            <div className="dl-detail__chips">
              <span className="dl-creep-card__tag">
                {(t.shop.categories as Record<string, string>)[item.category]}
              </span>
              <span className="dl-creep-card__tag dl-creep-card__tag--points">
                {stufe} / {maxStufe}
              </span>
              {/* Das Totem steht bei den Consumables, belegt aber einen Platz. */}
              {item.unique && <span className="dl-creep-card__tag">{t.shop.slotBadge}</span>}
            </div>

            <span className="dl-detail__text">{item.description}</span>

            {/*
              Ueberschrift und Werteliste sehen bei jedem Item gleich aus -
              auch beim Totem (Rueckmeldung vom 27.09.2026, iPad). Vorher stand
              dort ein ganzer Satz statt einer Ueberschrift, der auf dem Tablet
              umbrach und das Fenster hoeher machte. Das Totem hat keine
              Zahlenwirkung, also steht in seiner Zeile, was es tatsaechlich
              tut.
            */}
            <span className="dl-summary__title">
              {item.unique
                ? t.shop.uniqueTitle
                : t.shop.effectsTitle.replace('{n}', String(zeigeStufe))}
            </span>

            <dl className="dl-summary">
              {item.unique ? (
                <div className="dl-summary__row">
                  <dt>{t.shop.reviveLabel}</dt>
                  <dd>{t.shop.reviveValue.replace('{s}', String(REVIVE_TOTEM_RESPAWN_SECONDS))}</dd>
                </div>
              ) : (
                wirkung.map((zeile) => (
                  <div className="dl-summary__row" key={zeile.label}>
                    <dt>{zeile.label}</dt>
                    <dd>{zeile.value}</dd>
                  </div>
                ))
              )}
            </dl>

            {/*
              Die Lieferzeile ist immer im Aufbau, nur meistens unsichtbar:
              Sonst wuerde das Fenster in dem Moment hoeher, in dem ein Item
              unterwegs ist (Rueckmeldung vom 27.09.2026).
            */}
            <span
              className={
                'dl-detail__warning' +
                (besitz && besitz.deliverySecondsRemaining > 0 ? '' : ' dl-detail__warning--leer')
              }
              aria-hidden={besitz && besitz.deliverySecondsRemaining > 0 ? undefined : true}
            >
              {t.shop.delivery.replace(
                '{s}',
                String(Math.ceil(besitz?.deliverySecondsRemaining ?? 0)),
              )}
            </span>
          </>
        )}

        {trank && (
          <>
            <div className="dl-detail__head">
              <TrankZeichen trank={trank} />
              <span className="dl-detail__name">{trank.name}</span>
            </div>

            <div className="dl-detail__chips">
              <span className="dl-creep-card__tag">{t.shop.potionChip}</span>
              <span className="dl-creep-card__tag dl-creep-card__tag--points">
                {trankVorrat(trank.id)} / {trank.maxCharges}
              </span>
            </div>

            {/* Derselbe Aufbau wie bei einem Item, damit die Spalte gleich
                hoch bleibt: Beschreibung, Ueberschrift, Werteliste. */}
            <span className="dl-detail__text">{t.shop.potionText}</span>

            <span className="dl-summary__title">{t.shop.potionEffect}</span>

            <dl className="dl-summary">
              <div className="dl-summary__row">
                <dt>
                  {trank.restores === 'hp'
                    ? t.shop.effects.maxHpRatio
                    : t.shop.effects.maxManaRatio}
                </dt>
                <dd>{Math.round(trank.ratio * 100)} %</dd>
              </div>
              <div className="dl-summary__row">
                <dt>{t.shop.duration}</dt>
                <dd>{trank.durationSeconds}s</dd>
              </div>
            </dl>

            {/*
              Seit dem 05.10.2026 haben auch Traenke eine Lieferzeit. Steht
              einer aus, zeigt die Zeile die Restzeit; sonst bleibt sie als
              unsichtbarer Platzhalter stehen, damit das Fenster nicht
              springt.
            */}
            <span
              className={
                'dl-detail__warning' +
                (trankAnkunft(trank.id) > 0 ? '' : ' dl-detail__warning--leer')
              }
              aria-hidden={trankAnkunft(trank.id) > 0 ? undefined : true}
            >
              {t.shop.delivery.replace('{s}', String(Math.ceil(trankAnkunft(trank.id))))}
            </span>
          </>
        )}

        <div className="dl-detail__foot">
          {/*
            Der Verkaufsknopf ist immer im Aufbau. Erschiene er erst mit dem
            ersten gekauften Item, wuerde das Fenster genau dann hoeher.
          */}
          <button
            type="button"
            className={'dl-shop__sell' + (besitz ? '' : ' dl-shop__sell--leer')}
            aria-hidden={besitz ? undefined : true}
            disabled={!besitz}
            {...verkaufen}
          >
            {t.shop.sell} · {formatGold(sellValue(besitz?.investedGold ?? 0))}
          </button>

          <button
            type="button"
            className="dl-composer__send dl-detail__cta"
            disabled={gesperrt}
            {...kaufen}
          >
            <span className="dl-composer__send-label">{knopfText}</span>
            {!voll && <span className="dl-composer__send-cost">{formatGold(preis)}</span>}
            {grund && <span className="dl-composer__send-reason">{grund}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
